import { type HTMLAttributes } from 'react';
import { cn } from '@shared/lib/cn';

/**
 * Hairline-only skeleton — no shimmer (looks generic). Pulses opacity
 * between 60% and 100% at 1.6s per cycle. Disabled by `prefers-reduced-motion`
 * via the global media query in tokens.css.
 */
export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      aria-hidden
      className={cn(
        'rounded-md bg-sunken animate-skeleton-pulse',
        className,
      )}
      {...props}
    />
  );
}

/** Pre-fab shapes for common skeleton layouts. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }): JSX.Element {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn('h-3', i === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}

export function SkeletonRow({ count = 4 }: { count?: number }): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          <Skeleton className="h-10 w-10 rounded-pill" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}
